<template>
  <section>
    <label>{{ $t('message.booking.checkout_step.payment_method') }}</label>
    <!-- Payment with credit card -->
    <div class="card shadow-sm">
      <div class="card-body p-3">
        <div class="exp">
          <div class="checkbox">
            <input
              type="radio"
              id="paymentCreditDebit"
              name="paymentMethod"
              value="credit_card"
              v-model="paymentMethod"
            />
            <label for="paymentCreditDebit" class="mb-0">
              <span><!-- This span is needed to create the "checkbox" element --></span>
              {{ $t('message.booking.payment_step.pay_by_credit_debit_card') }}
            </label>
            <div class="text-danger text-remark pt-1 pl-4 ml-2" v-if="displayCreditCardRemark">
              {{ $t('message.professional_tip.credit_card_remark') }}
            </div>
          </div>
        </div>
        <div class="pt-3" v-if="paymentMethod === 'credit_card'">
          <user-credit-cards :canEditCard="false"
                             :action="'new_additional_hours_booking_payment'"
                             @onSelected="emitSelectingCreditCard"
                             @onAddNewCard="emitSavingTempData"></user-credit-cards>
        </div>
      </div>
    </div>

    <!-- Payment with bank transfer -->
    <div class="card shadow-sm" v-if="displayBankTransfer">
      <div class="card-body p-3">
        <div class="exp">
          <div class="checkbox">
            <input
              type="radio"
              id="bankTransfer"
              ref="bankTransfer"
              name="paymentMethod"
              value="bank_transfer"
              v-model="paymentMethod"
            />
            <label for="bankTransfer" class="mb-0">
              <span><!-- This span is needed to create the "checkbox" element --></span>
              {{ $t('message.booking.payment_step.pay_by_bank_transfer') }}
            </label>
          </div>
        </div>
        <div class="payment-method" v-if="paymentMethod === 'bank_transfer'">
          <div class="row mt-3">
            <div class="col-12 p-0">
              <div>
                <div>
                  <img :src="globalState.bankAccountData.image_url" width="160" class="mb-2">
                </div>
                <div>{{ $t('message.bank_transfer.account_name') }}: {{ globalState.bankAccountData | multiLangName
                  }}
                </div>
                <div class="d-flex align-items-center justify-content-center">
                  <div class="flex-grow-1">
                    <div>{{ $t('message.bank_transfer.account') }}: {{ globalState.bankAccountData.account_number }}
                    </div>
                  </div>
                  <div class="text-right">
                    <button class="btn btn-outline-info"
                            @click="isCopied = true"
                            v-clipboard:copy="globalState.bankAccountData.account_number">
                      {{ isCopied ? $t('message.button.copied') : $t('message.button.copy') }}
                    </button>
                  </div>
                </div>
              </div>

              <hr/>

              <label>
                {{ $t('message.bank_transfer.attach_transfer_picture') }}
              </label>
              <div class="mt-1">
                <vue-dropzone ref="vueDropzone"
                              id="dropzone"
                              class="dropzone-dashed"
                              :options="dropzoneOptions"
                              @vdropzone-file-added="handleFileUpload"
                              @vdropzone-max-files-exceeded="vueDropZoneMaxFilesExceeded"/>
                <div class="text-remark py-2">
                  {{ $t('message.bank_transfer.transfer_picture_remark01') }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Payment with promptpay -->
    <div class="card shadow-sm" v-if="displayPromptpay">
      <div class="card-body p-3">
        <div class="exp" v-if="globalState.configData.promptpay.status === 1">
          <div class="checkbox">
            <input
              type="radio"
              id="promptpay"
              ref="promptpay"
              name="paymentMethod"
              value="promptpay"
              v-model="paymentMethod"
            />
            <label for="promptpay" class="mb-0">
              <span><!-- This span is needed to create the "checkbox" element --></span>
              {{ $t('message.booking.payment_step.pay_by_promptpay') }}
              <img src="/static/img/thai_qr_payment.png" width="90" class="rounded ml-2"/>
            </label>
          </div>
        </div>
        <div v-else>
          <div class="d-flex align-items-center justify-content-start">
            <div>
              <i class="fas fa-circle fa-lg -status-offline"></i>
            </div>
            <div class="pl-3">
              {{ $t('message.booking.payment_step.pay_by_promptpay') }}
            </div>
            <div>
              <img src="/static/img/thai_qr_payment.png" width="90" class="rounded ml-2"/>
            </div>
          </div>
          <div class="pt-3 text-small">
            {{ ($i18n.locale === 'th') ? globalState.configData.promptpay.remark_th :
            globalState.configData.promptpay.remark_en }}
          </div>
        </div>
      </div>
    </div>
    <div v-show="paymentMethod === 'promptpay' && allowLoadQrCode">
      <qr-code-payment ref="qrCodePayment" @checkQrPayment="checkQrPayment"/>
      <hr/>
      <div class="text-remark">
        {{ $t('message.promptpay.payment_remark') }}
      </div>
    </div>

  </section>
</template>

<script>
  import UserCreditCards from '../user/payment/UserCreditCards'
  import QrCodePayment from '../qrPayment/QrCodePayment'
  import vue2Dropzone from 'vue2-dropzone'
  import { mapGetters } from 'vuex'
  import axios from 'axios'

  export default {
    props: {
      displayCreditCardRemark: {
        type: Boolean,
        default: false
      },
      displayBankTransfer: {
        type: Boolean,
        default: true
      },
      displayPromptpay: {
        type: Boolean,
        default: true
      }
    },
    components: {
      UserCreditCards,
      QrCodePayment,
      vueDropzone: vue2Dropzone
    },
    computed: {
      ...mapGetters({
        globalState: 'globalVal/getState'
      })
    },
    watch: {
      paymentMethod () {
        this.$emit('onSelectedPaymentMethod', this.paymentMethod)
      }
    },
    data () {
      return {
        allowLoadQrCode: true,
        paymentMethod: null,
        qrPaymentData: null,
        checkingQrPayment: null,
        qrResponse: null,
        dropzoneOptions: {
          acceptedFiles: 'image/*',
          url: 'https://beneat.co',
          thumbnailWidth: 160,
          thumbnailHeight: 160,
          maxFilesize: 5,
          dictDefaultMessage: `<i class="fal fa-cloud-upload"></i> ${this.$i18n.t('message.button.attach_slip_here')}`,
          addRemoveLinks: true,
          autoProcessQueue: false,
          maxFiles: 1
        },
        isCopied: false
      }
    },
    methods: {
      setAllowLoadQrCode (status) {
        if (!status) {
          clearInterval(this.checkingQrPayment)
        }
        this.allowLoadQrCode = status
      },
      setPaymentMethod (paymentMethod) {
        this.paymentMethod = paymentMethod
      },
      performClearInterval () {
        clearInterval(this.checkingQrPayment)
      },
      emitSavingTempData () {
        this.$emit('onRequestedSavingTempData', true)
      },
      emitSelectingCreditCard (userCreditCard) {
        const data = {
          payment_method: 'credit_card',
          omise_customer_id: userCreditCard.omise_customer_id
        }
        this.$emit('onSelectedCreditCard', data)
      },
      handleFileUpload (file) {
        this.$emit('onSelectedFileUpload', file)
      },
      vueDropZoneMaxFilesExceeded (file) {
        this.$refs.vueDropzone.removeAllFiles()
        this.$refs.vueDropzone.addFile(file)
      },
      updateQrPaymentData (qrPaymentData) {
        this.performClearInterval()
        if (this.allowLoadQrCode) {
          this.$refs.qrCodePayment.loadQrCode(qrPaymentData)
        }
      },
      checkQrPayment (response) {
        // Emit qr payment response
        this.$emit('onRequestedUpdatingQrResponse', response)
        this.qrResponse = response
        // Clear old interval before starting a new one
        if (this.checkingQrPayment !== null) {
          this.performClearInterval()
          this.checkingQrPayment = null
        }
        this.checkingQrPayment = setInterval(() => {
          const url = '/qr-payments'
          axios.get(url, {
            params: { transaction_code: this.qrResponse.partnerTxnUid }
          }).then(async (res) => {
            const response = res.data
            if (!response.error) {
              if (response.qr_payment && response.qr_payment.status_code) {
                if (response.qr_payment.status_code === '00') {
                  this.performClearInterval()
                  // Display success dialog
                  this.$emit('onSuccessQrPayment', this.$i18n.t('message.payment_method.payment_success'))
                } else if (response.qr_payment.status_code === '10') {
                  this.performClearInterval()
                  const alertMessage = `Error: ${response.qr_payment.error_description}`
                  this.$swal({ text: alertMessage, type: 'error' })
                  // if error set qr code timeout to be expired
                  this.$refs.qrCodePayment.finishTimer()
                }
              }
            } else {
              this.performClearInterval()
            }
          }).catch(error => {
            console.log('error', error)
          })
        }, 7000)
      }
    },
    beforeDestroy () {
      this.performClearInterval()
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/order/PaymentMethods.vue